import {Column} from "primereact/column";
import {DataTable, type DataTableSelectionSingleChangeEvent, type DataTableStateEvent} from "primereact/datatable";
import {useEffect, useMemo, useState} from "react";

import {SnapshotType} from "@reva/shared/valuation-data";

import type {SearchSnapshotItem} from "../../api/payload-types/snapshot-search";
import ErrorIndicator from "../../component/ErrorIndicator";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectPersistenceInitialized, selectPersistenceLoading, selectRevaId} from "../../redux/persistenceSlice";
import {fetchPersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {
    SNAPSHOTS_PAGE_SIZE,
    type SnapshotScope,
    selectSnapshots,
    selectSnapshotsCount,
    selectSnapshotsLoading,
    selectSnapshotsPageIndex,
    selectSnapshotsQueryError,
} from "../../redux/searchSnapshotsSlice";
import {initializeSnapshotsThunk, loadSnapshotsPageThunk, reloadSnapshotsThunk} from "../../redux/searchSnapshotsThunk";
import {selectFreeText} from "../../redux/searchValuationsSlice";
import {selectDarkTheme} from "../../redux/themeSlice";
import {getSnapshotTypeColors} from "../../util/chartUtils";
import {formatDateAndTime} from "../../util/dateUtil";
import {highlightFreeText} from "../../util/freeTextHighlight";
import SnapshotStatisticsPanel from "./SnapshotStatisticsPanel";
import {ColorDotLabel, FilterCheckboxColumnHeader, StatisticsToggleButton, TabControlCluster} from "./startPageWidgets";
import TabHint, {NoFilterHitsHint} from "./TabHint";

interface DoneTabProps {
    className?: string;
    /** Which snapshots to show: "own" (default, own Historie/DONE tab) or "all" (Admin tab). */
    scope?: SnapshotScope;
}

/**
 * #19113: Cross-valuation snapshot list — every snapshot ever created (across all valuations), newest first.
 * Selecting a row loads the frozen snapshot (read-only view). Used both for the user's own "Historie" (DONE)
 * tab (scope "own") and, via {@link scope} = "all", for the Admin tab's "Historie" sub-tab (all users'
 * snapshots). The scope is the single knob: the own tab shows the activity statistics above the list, the
 * Admin tab instead shows an author column and a reload button (its statistics live in a separate sub-tab).
 * Data is loaded on demand, on first activation; each scope keeps its own slice state, so switching
 * between the own and Admin Historie never re-fetches or shows the other scope's data.
 */
export default function DoneTab({className, scope = "own"}: DoneTabProps) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    // "all" scope == the admin "Historie" sub-tab; it drives every own-vs-admin presentation difference below
    // (statistics panel vs. author column + reload button, and the empty-table message).
    const isAdmin = scope === "all";

    const freeText = useRevaSelector(selectFreeText);
    const persistenceLoading = useRevaSelector(selectPersistenceLoading);
    const persistenceInitialized = useRevaSelector(selectPersistenceInitialized);
    const persistenceRevaId = useRevaSelector(selectRevaId);

    // Per-Scope-State (wie die Bewertungs-Tabs): jede Instanz liest ausschließlich die Daten ihres
    // eigenen Scopes — kein Flackern mit fremdem Inhalt und kein Re-Fetch beim Scope-Wechsel mehr.
    const loading = useRevaSelector((state) => selectSnapshotsLoading(state, scope));
    const count = useRevaSelector((state) => selectSnapshotsCount(state, scope));
    const pageIndex = useRevaSelector((state) => selectSnapshotsPageIndex(state, scope));
    const snapshots = useRevaSelector((state) => selectSnapshots(state, scope));
    const queryError = useRevaSelector((state) => selectSnapshotsQueryError(state, scope));

    const isDarkTheme = useRevaSelector(selectDarkTheme);

    // Typ-Filter der Tabelle: beide Checkboxen standardmäßig aus (= kein Filter, alle Zeilen sichtbar)
    const [showFinal, setShowFinal] = useState(false);
    const [showPreFinal, setShowPreFinal] = useState(false);

    // Statistik-Panel nur im eigenen DONE-Tab; es lädt seine Daten beim ersten Aufklappen selbst.
    const [showStats, setShowStats] = useState(false);

    // On-demand loading: this component is mounted only when the tab is activated for the first time;
    // the thunk loads only this scope's list and only once (each scope keeps its data). Statistics are
    // never loaded here — {@link SnapshotStatisticsPanel} loads them itself once it is shown.
    useEffect(() => {
        void dispatch(initializeSnapshotsThunk(scope));
    }, [dispatch, scope]);

    // Gleiche Farbquelle wie das Statistik-Panel darüber (SnapshotStatisticsPanel), damit die
    // Checkbox-Swatches exakt zu den Balken im Diagramm passen (inkl. Dark-Theme-Variante).
    const {colorFinal, colorPreFinal} = getSnapshotTypeColors(isDarkTheme);

    const onPage = (event: DataTableStateEvent) => {
        void dispatch(loadSnapshotsPageThunk(scope, event.page ?? 0));
    };

    const onLoadSnapshot = (event: DataTableSelectionSingleChangeEvent<SearchSnapshotItem[]>) => {
        const snapshot = event?.value;
        if (snapshot) {
            // "<revaId>.<number>" loads the frozen snapshot instead of the live valuation
            void dispatch(fetchPersistence({id: `${snapshot.revaId}.${snapshot.number}`}));
        }
    };

    // A valuation can have several snapshots, so the row key must combine revaId and number.
    // Memoized: ohne useMemo bekäme die DataTable bei jedem unabhängigen Re-Render (Checkbox,
    // Statistik-Toggle, Theme) ein frisches Array frischer Objekte und würde den ganzen Body neu aufbauen.
    const rows = useMemo(
        () => snapshots.map((snapshot) => ({...snapshot, rowKey: `${snapshot.revaId}:${snapshot.number}`})),
        [snapshots],
    );

    // Rein client-seitiger Filter auf die aktuell geladene Seite (kein Server-/Thunk-Change,
    // Pagination/Count bleiben unverändert): sind beide Checkboxen an ODER beide aus, gilt das
    // als "kein Filter" (alle Zeilen); ist genau eine an, werden nur Zeilen dieses Typs gezeigt.
    // Wirkt ebenso auf das Statistik-Panel (visibleTypes).
    const onlyType = showFinal === showPreFinal ? undefined : showFinal ? SnapshotType.Final : SnapshotType.PreFinal;
    const filteredRows = useMemo(
        () => (onlyType ? rows.filter((row) => row.type === onlyType) : rows),
        [rows, onlyType],
    );

    // Spalten-Header "Typ": statt eines Badges pro Zeile trägt der Header selbst je eine Checkbox pro
    // Snapshot-Typ zum Ein-/Ausblenden (gleicher Mechanismus wie der "Status"-Header der
    // Bewertungslisten, siehe FilterCheckboxColumnHeader).
    const typeFilters = [
        {type: SnapshotType.PreFinal, color: colorPreFinal, checked: showPreFinal, setChecked: setShowPreFinal},
        {type: SnapshotType.Final, color: colorFinal, checked: showFinal, setChecked: setShowFinal},
    ];
    const typeColumnHeader = (
        <FilterCheckboxColumnHeader
            title={t("start.snapshots.type")}
            items={typeFilters.map(({type, color, checked, setChecked}) => ({
                inputId: `snapshotTypeFilter${type}`,
                color,
                checked,
                tooltip: t(`snapshotType.${type}`),
                onToggle: () => setChecked(!checked),
            }))}
        />
    );

    // Jede Zeile schreibt ihren Snapshot-Typ ("Vorläufige Finalisierung"/"Finalisierung") mit dem
    // passenden Farbpunkt aus — in beiden Historien (eigene DONE- wie Admin-"Historie").
    const typeBodyTemplate = (data: SearchSnapshotItem) => (
        <ColorDotLabel
            color={data.type === SnapshotType.Final ? colorFinal : colorPreFinal}
            label={t(`snapshotType.${data.type}`)}
        />
    );

    // Bei aktivem Freitext ohne Treffer: nur eine kurze "keine Treffer"-Zeile statt Hinweis + leerer
    // Tabelle (wie in ValuationsTable).
    if (freeText != null && !loading && count === 0 && !queryError) {
        return (
            <div id="snapshotsTab" className={className}>
                <NoFilterHitsHint freeText={freeText} />
            </div>
        );
    }

    return (
        <div id="snapshotsTab" className={className}>
            {/* Einheitliche Kontroll-Ecke oben rechts (Reload immer ganz rechts, gleiche Stelle wie auf
                allen Tabs). Nur im eigenen DONE-Tab steht links davon der Statistik-Umschalter; die
                Admin-"Historie" hat kein Statistik-Panel, nur den Reload. */}
            <TabHint
                action={
                    <TabControlCluster
                        statsToggle={
                            !isAdmin ? (
                                <StatisticsToggleButton active={showStats} onToggle={() => setShowStats((s) => !s)} />
                            ) : undefined
                        }
                        onReload={() => void dispatch(reloadSnapshotsThunk(scope))}
                    />
                }
            >
                {isAdmin ? t("start.admin.history.hint") : t("start.snapshots.hint")}
            </TabHint>
            {!isAdmin && showStats && (
                <SnapshotStatisticsPanel
                    className="mb-6"
                    scope={scope}
                    visibleTypes={onlyType ? [onlyType] : undefined}
                />
            )}
            <ErrorIndicator
                titleI18nKey="start.snapshots.queryError"
                errorObject={queryError}
                hasContactLink={true}
                classNameContactLink="mb-6"
            />
            {/* key=freeText: PrimeReact cached die Column-Bodies — ohne Remount würden die
                Treffer-Highlights einem Filterwechsel hinterherhinken (Marks fehlen/bleiben stehen). */}
            <DataTable<SearchSnapshotItem[]>
                key={freeText ?? ""}
                size="small"
                lazy
                paginator
                rows={SNAPSHOTS_PAGE_SIZE}
                totalRecords={count}
                loading={loading || persistenceLoading}
                disabled={!persistenceInitialized || !!persistenceRevaId}
                value={filteredRows}
                onPage={onPage}
                first={pageIndex * SNAPSHOTS_PAGE_SIZE}
                dataKey="rowKey"
                selectionMode="single"
                onSelectionChange={onLoadSnapshot}
                stripedRows
                emptyMessage={t(isAdmin ? "start.admin.history.empty" : "start.snapshots.empty")}
            >
                <Column
                    header={t("start.snapshots.date")}
                    field="date"
                    body={(data: SearchSnapshotItem) => formatDateAndTime(data.date, language)}
                />
                <Column header={typeColumnHeader} body={typeBodyTemplate} />
                {isAdmin && (
                    <Column
                        header={t("start.snapshots.by")}
                        field="user"
                        body={(data: SearchSnapshotItem) => highlightFreeText(data.userFullName ?? data.user, freeText)}
                    />
                )}
                <Column
                    header={t("start.snapshots.address")}
                    field="address"
                    body={(data: SearchSnapshotItem) => highlightFreeText(data.address, freeText)}
                />
                <Column
                    header={t("start.snapshots.segment")}
                    field="segment"
                    body={(data: SearchSnapshotItem) =>
                        data.segment ? t(`object.segments.${data.segment}`, data.segment) : null
                    }
                />
                <Column
                    header={t("start.snapshots.comment")}
                    field="comment"
                    body={(data: SearchSnapshotItem) => highlightFreeText(data.comment, freeText)}
                />
                <Column
                    header={t("start.snapshots.id")}
                    field="revaId"
                    body={(data: SearchSnapshotItem) => (
                        <span>
                            {highlightFreeText(data.revaId, freeText)}
                            <span className="text-surface-400 dark:text-surface-400"> #{data.number}</span>
                        </span>
                    )}
                />
            </DataTable>
        </div>
    );
}
